import { Component, OnDestroy, OnInit, inject, signal } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { CurrentContextService } from '../../core/context/current-context.service';
import { LiveUpdateService } from '../../core/live-updates/live-update.service';
import { LocalizationService } from '../../core/localization/localization.service';
import { AinDatePipe } from '../../shared/design-system/ain-date.pipe';
import { InfoHintComponent } from '../../shared/design-system/info-hint.component';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import { InsuranceApiService, InsuranceProducerListItem } from './insurance-api.service';
import {
  INSURANCE_LIST_TOOLBAR_STYLES,
  SortOrder,
  compareSortValues,
  downloadCsv,
  liveStatusKey,
  subscribeInsuranceOperationsLiveUpdates,
  toggleSortField
} from './insurance-list-page.utils';

type ProducerSortField = 'code' | 'name' | 'producerType' | 'licenseNumber' | 'licenseExpiryUtc' | 'status';

@Component({
  standalone: true,
  selector: 'ain-insurance-producers-shell',
  imports: [AinDatePipe, AinPageComponent, AinStateComponent, InfoHintComponent, FormsModule, TableModule, ButtonModule, InputTextModule, SelectModule],
  template: `
    <ain-page
      [title]="t('insurance.producers.title')"
      [description]="t('insurance.producers.description')">
      <ain-info-hint
        page-title-addon
        [titleKey]="'insurance.producers.help.page.title'"
        [descriptionKey]="'insurance.producers.help.page.description'"
        [bulletKeys]="[
          'insurance.producers.help.page.bullets.scope',
          'insurance.producers.help.page.bullets.registry',
          'insurance.producers.help.page.bullets.selection'
        ]" />
      @if (loading()) {
        <ain-state type="loading" [title]="t('common.loading')" />
      } @else if (permissionDenied()) {
        <ain-state type="error" [title]="t('insurance.states.noPermission')" [message]="t('insurance.states.noPermissionMessage')" />
      } @else {
        <section class="card panel">
          <div class="toolbar">
            <button pButton type="button" [label]="t('insurance.producers.actions.create')" (click)="openCreate()"></button>
            <button pButton type="button" severity="secondary" [label]="t('insurance.actions.refresh')" (click)="load()"></button>
            <button pButton type="button" severity="secondary" [label]="t('insurance.actions.bulk')" (click)="applyBulk()"></button>
            <button pButton type="button" severity="secondary" [label]="t('insurance.actions.export')" (click)="exportRows()"></button>
            <button pButton type="button" severity="secondary" [label]="t('insurance.actions.open')" [disabled]="selectedRows().length !== 1" (click)="openSelected()"></button>
            <span class="live-status" [class]="liveUpdateService.connectionState()">
              {{ t(liveStatusKey(liveUpdateService.connectionState())) }}
            </span>
          </div>

          <details class="filters" [open]="true">
            <summary>
              <span>{{ t('insurance.filters.title') }}</span>
              <ain-info-hint
                [titleKey]="'insurance.producers.help.listFilters.title'"
                [descriptionKey]="'insurance.producers.help.listFilters.description'"
                [bulletKeys]="[
                  'insurance.producers.help.listFilters.bullets.search',
                  'insurance.producers.help.listFilters.bullets.fields',
                  'insurance.producers.help.listFilters.bullets.selection'
                ]" />
            </summary>
            <div class="filter-grid">
              <label>
                <span>{{ t('insurance.producers.filters.search') }}</span>
                <input pInputText [value]="search()" (input)="onSearch($event)" />
              </label>
              <label>
                <span>{{ t('insurance.producers.filters.type') }}</span>
                <p-select
                  [options]="producerTypeOptions"
                  optionValue="value"
                  [(ngModel)]="selectedType"
                  (ngModelChange)="onTypeChanged($event)"
                  [showClear]="true"
                  appendTo="body">
                  <ng-template pTemplate="selectedItem" let-item>{{ item ? producerTypeLabel(item.value) : t('common.all') }}</ng-template>
                  <ng-template pTemplate="item" let-item>{{ producerTypeLabel(item.value) }}</ng-template>
                </p-select>
              </label>
              <label>
                <span>{{ t('insurance.producers.filters.status') }}</span>
                <p-select
                  [options]="statusOptions"
                  optionValue="value"
                  [(ngModel)]="selectedStatus"
                  (ngModelChange)="onStatusChanged($event)"
                  [showClear]="true"
                  appendTo="body">
                  <ng-template pTemplate="selectedItem" let-item>{{ item ? producerStatusLabel(item.value) : t('common.all') }}</ng-template>
                  <ng-template pTemplate="item" let-item>{{ producerStatusLabel(item.value) }}</ng-template>
                </p-select>
              </label>
            </div>
          </details>

          @if (filteredRows().length === 0) {
            <ain-state type="empty" [title]="t('insurance.producers.states.empty')" [message]="t('insurance.producers.states.emptyMessage')" />
          } @else {
            <p-table
              [value]="filteredRows()"
              dataKey="id"
              [paginator]="true"
              [rows]="10"
              [selection]="selectedRows()"
              (selectionChange)="onSelectionChange($event)">
              <ng-template pTemplate="header">
                <tr>
                  <th style="width:44px"><p-tableHeaderCheckbox /></th>
                  <th><button type="button" class="sort-btn" (click)="sortBy('code')">{{ t('insurance.producers.fields.code') }}</button></th>
                  <th><button type="button" class="sort-btn" (click)="sortBy('name')">{{ t('insurance.producers.fields.name') }}</button></th>
                  <th><button type="button" class="sort-btn" (click)="sortBy('producerType')">{{ t('insurance.producers.fields.type') }}</button></th>
                  <th><button type="button" class="sort-btn" (click)="sortBy('licenseNumber')">{{ t('insurance.producers.fields.licenseNumber') }}</button></th>
                  <th><button type="button" class="sort-btn" (click)="sortBy('licenseExpiryUtc')">{{ t('insurance.producers.fields.licenseExpiry') }}</button></th>
                  <th><button type="button" class="sort-btn" (click)="sortBy('status')">{{ t('columns.status') }}</button></th>
                </tr>
              </ng-template>
              <ng-template pTemplate="body" let-row>
                <tr>
                  <td><p-tableCheckbox [value]="row" /></td>
                  <td><button type="button" class="link-btn" (click)="openDetail(row.id)">{{ row.code }}</button></td>
                  <td>{{ row.name }}</td>
                  <td>{{ producerTypeLabel(row.producerType) }}</td>
                  <td>{{ row.licenseNumber }}</td>
                  <td>{{ row.licenseExpiryUtc | ainDate }}</td>
                  <td>{{ producerStatusLabel(row.status) }}</td>
                </tr>
              </ng-template>
            </p-table>
          }

          @if (info()) { <p class="info">{{ info() }}</p> }
          @if (error()) { <p class="error">{{ error() }}</p> }
        </section>
      }
    </ain-page>
  `,
  styles: [`
    .card { border: 1px solid #e5e7eb; border-radius: 12px; background: #fff; padding: 12px; }
    .panel { display: grid; gap: 10px; }
    .filters { border: 1px solid #e5e7eb; border-radius: 10px; padding: 8px 10px; background: #f9fafb; }
    .filters summary { display: flex; align-items: center; gap: 8px; }
    .filter-grid { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 10px; margin-top: 8px; }
    .filter-grid label { display: grid; gap: 6px; }
    .link-btn { border: 0; background: none; color: #1d4ed8; cursor: pointer; font-weight: 600; padding: 0; }
    .error { color: #b91c1c; }
    @media (max-width: 760px) { .filter-grid { grid-template-columns: 1fr; } }
    ${INSURANCE_LIST_TOOLBAR_STYLES}
  `]
})
export class InsuranceProducersShellComponent implements OnInit, OnDestroy {
  private readonly api = inject(InsuranceApiService);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly currentContext = inject(CurrentContextService);
  readonly liveUpdateService = inject(LiveUpdateService);
  private readonly localization = inject(LocalizationService);
  private liveUnsubscribe: (() => Promise<void>) | null = null;

  readonly loading = signal(true);
  readonly error = signal('');
  readonly info = signal('');
  readonly permissionDenied = signal(false);
  readonly rows = signal<InsuranceProducerListItem[]>([]);
  readonly selectedRows = signal<InsuranceProducerListItem[]>([]);
  readonly search = signal('');
  readonly sortField = signal<ProducerSortField>('code');
  readonly sortOrder = signal<SortOrder>(1);
  selectedType: string | null = null;
  selectedStatus: string | null = null;

  liveStatusKey = liveStatusKey;

  readonly producerTypeOptions = [
    { value: 'Agent', labelKey: 'insurance.producers.types.agent' },
    { value: 'Broker', labelKey: 'insurance.producers.types.broker' },
    { value: 'Employee', labelKey: 'insurance.producers.types.employee' },
    { value: 'Bancassurance', labelKey: 'insurance.producers.types.bancassurance' }
  ];

  readonly statusOptions = [
    { value: 'Active', labelKey: 'insurance.producers.status.active' },
    { value: 'Suspended', labelKey: 'insurance.producers.status.suspended' },
    { value: 'Terminated', labelKey: 'insurance.producers.status.terminated' }
  ];

  ngOnInit(): void {
    this.search.set(this.route.snapshot.queryParamMap.get('q') ?? '');
    this.load();
    void this.startLiveUpdates();
  }

  ngOnDestroy(): void {
    void this.liveUnsubscribe?.();
  }

  load(): void {
    this.loading.set(true);
    this.api.listProducers().subscribe({
      next: rows => {
        this.rows.set(rows);
        this.error.set('');
        this.info.set('');
        this.permissionDenied.set(false);
        this.loading.set(false);
      },
      error: err => {
        this.rows.set([]);
        this.permissionDenied.set(err?.status === 403);
        this.error.set(err?.status === 403 ? '' : this.t('insurance.states.loadError'));
        this.loading.set(false);
      }
    });
  }

  filteredRows(): InsuranceProducerListItem[] {
    const value = this.search().trim().toLowerCase();
    const filtered = this.rows().filter(row => {
      if (this.selectedType && row.producerType !== this.selectedType) {
        return false;
      }
      if (this.selectedStatus && row.status !== this.selectedStatus) {
        return false;
      }
      if (!value) {
        return true;
      }
      return row.code.toLowerCase().includes(value)
        || row.name.toLowerCase().includes(value)
        || row.licenseNumber.toLowerCase().includes(value)
        || this.producerTypeLabel(row.producerType).toLowerCase().includes(value)
        || this.producerStatusLabel(row.status).toLowerCase().includes(value)
        || row.producerType.toLowerCase().includes(value)
        || row.status.toLowerCase().includes(value);
    });

    const field = this.sortField();
    const order = this.sortOrder();
    return [...filtered].sort((left, right) => compareSortValues(left[field], right[field], order));
  }

  onSearch(event: Event): void {
    this.search.set((event.target as HTMLInputElement).value ?? '');
    void this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { q: this.search().trim() || null },
      queryParamsHandling: 'merge'
    });
  }

  onTypeChanged(value: string | null): void {
    this.selectedType = value;
  }

  onStatusChanged(value: string | null): void {
    this.selectedStatus = value;
  }

  producerTypeLabel(value: string): string {
    const match = this.producerTypeOptions.find(item => item.value === value);
    return match ? this.t(match.labelKey) : value;
  }

  producerStatusLabel(value: string): string {
    const match = this.statusOptions.find(item => item.value === value);
    return match ? this.t(match.labelKey) : value;
  }

  onSelectionChange(selection: InsuranceProducerListItem[] | InsuranceProducerListItem | null): void {
    this.selectedRows.set(Array.isArray(selection) ? selection : selection ? [selection] : []);
  }

  openSelected(): void {
    const row = this.selectedRows()[0];
    if (row) {
      this.openDetail(row.id);
    }
  }

  openDetail(id: string): void {
    void this.router.navigate(['/insurance/producers', id], {
      queryParams: { q: this.search().trim() || null }
    });
  }

  openCreate(): void {
    void this.router.navigate(['/insurance/producers/new']);
  }

  applyBulk(): void {
    if (this.selectedRows().length === 0) {
      this.info.set(this.t('insurance.actions.bulkNothingSelected'));
      return;
    }

    this.info.set(this.t('insurance.actions.bulkQueued'));
  }

  exportRows(): void {
    const rows = this.filteredRows();
    if (rows.length === 0) {
      this.info.set(this.t('insurance.actions.exportEmpty'));
      return;
    }

    downloadCsv(
      'insurance-producers.csv',
      ['Code', 'Name', 'ProducerType', 'LicenseNumber', 'LicenseExpiryUtc', 'Status'],
      rows.map(item => [
        item.code,
        item.name,
        item.producerType,
        item.licenseNumber,
        item.licenseExpiryUtc ?? '',
        item.status
      ]));
    this.info.set(this.t('insurance.actions.exportReady'));
  }

  sortBy(field: ProducerSortField): void {
    const next = toggleSortField(field, this.sortField(), this.sortOrder());
    this.sortField.set(next.field);
    this.sortOrder.set(next.order);
  }

  private async startLiveUpdates(): Promise<void> {
    const tenantId = this.currentContext.context()?.tenantId;
    if (!tenantId) {
      return;
    }

    const live = await subscribeInsuranceOperationsLiveUpdates(tenantId, this.liveUpdateService, () => this.load());
    this.liveUnsubscribe = live.unsubscribe;
  }

  t(key: string): string {
    return this.localization.translate(key);
  }
}
